import {
  AccountType,
  getV1CompaniesCompanyIdAccounts,
  patchV1CompaniesCompanyIdAccountsId,
  postV1CompaniesCompanyIdAccounts,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';

import { Account } from '~hooks/useAccount/models';
import { CSVColumn } from '~utils/csv/csvProcessor';
import { fetchAllApiPages } from '~utils/fetchAllPages';
import { toE164PhoneNumber } from '~utils/utilFunctions';

import { getPhoneValidationErrors } from './phoneValidationErrors';
import { EntityCSVConfig } from './types';

const customersColumns: CSVColumn[] = [
  {
    key: 'id',
    header: 'id',
    required: false,
    exampleValue: '',
    description: 'Existing ID (for updates only)',
  },
  {
    key: 'name',
    header: 'name',
    required: true,
    exampleValue: 'Acme Corp',
    description: 'Customer/company name',
  },
  {
    key: 'external_id',
    header: 'external_id',
    required: false,
    exampleValue: 'CUST-001',
    description: 'Your internal reference ID',
  },
  {
    key: 'department',
    header: 'department',
    required: false,
    exampleValue: '',
    description: 'Department (must exist in system)',
  },
  {
    key: 'accounting_id',
    header: 'accounting_id',
    required: false,
    exampleValue: '',
    description: 'Accounting system reference',
  },
  {
    key: 'primary_contact_first_name',
    header: 'primary_contact_first_name',
    required: false,
    exampleValue: 'John',
    description: 'Primary contact first name',
  },
  {
    key: 'primary_contact_last_name',
    header: 'primary_contact_last_name',
    required: false,
    exampleValue: 'Doe',
    description: 'Primary contact last name',
  },
  {
    key: 'primary_contact_email',
    header: 'primary_contact_email',
    required: false,
    exampleValue: 'john@example.com',
    description: 'Primary contact email',
  },
  {
    key: 'primary_contact_phone',
    header: 'primary_contact_phone',
    required: false,
    exampleValue: '+14155551234',
    description:
      'US numbers like 555-555-5555 or (555) 555-5555; other countries start with + and the country code (e.g., +14155551234)',
  },
  {
    key: 'billing_contact_name',
    header: 'billing_contact_name',
    required: false,
    exampleValue: 'Jane Doe',
    description: 'Billing contact name',
  },
  {
    key: 'billing_contact_email',
    header: 'billing_contact_email',
    required: false,
    exampleValue: 'jane@example.com',
    description: 'Billing contact email',
  },
  {
    key: 'billing_contact_phone',
    header: 'billing_contact_phone',
    required: false,
    exampleValue: '+14155551235',
    description:
      'Billing contact phone. US numbers like 555-555-5555 or (555) 555-5555; other countries start with + and the country code (e.g., +14155551234)',
  },
  {
    key: 'address_line_1',
    header: 'address_line_1',
    required: false,
    exampleValue: '123 Main St',
    description: 'Street address',
  },
  {
    key: 'address_line_2',
    header: 'address_line_2',
    required: false,
    exampleValue: '',
    description: 'Suite, unit, etc.',
  },
  {
    key: 'address_city',
    header: 'address_city',
    required: false,
    exampleValue: 'Austin',
    description: 'City',
  },
  {
    key: 'address_state',
    header: 'address_state',
    required: false,
    exampleValue: 'TX',
    description: '2-letter state code',
  },
  {
    key: 'address_zip',
    header: 'address_zip',
    required: false,
    exampleValue: '78701',
    description: 'ZIP code',
  },
  {
    key: 'billing_address_line_1',
    header: 'billing_address_line_1',
    required: false,
    exampleValue: '',
    description: 'Billing street address',
  },
  {
    key: 'billing_address_city',
    header: 'billing_address_city',
    required: false,
    exampleValue: '',
    description: 'Billing city',
  },
  {
    key: 'billing_address_state',
    header: 'billing_address_state',
    required: false,
    exampleValue: '',
    description: 'Billing state (2-letter code)',
  },
  {
    key: 'billing_address_zip',
    header: 'billing_address_zip',
    required: false,
    exampleValue: '',
    description: 'Billing ZIP code',
  },
  {
    key: 'notes',
    header: 'notes',
    required: false,
    exampleValue: '',
    description: 'Additional notes (max 350 chars)',
  },
];

export const createCustomersConfig = (
  getDepartmentIdByName: (name: string) => string | undefined,
): EntityCSVConfig<Account> => ({
  entityType: 'customers',
  columns: customersColumns,

  fetchAll: (companyId: string) =>
    fetchAllApiPages(
      (query) =>
        getV1CompaniesCompanyIdAccounts({
          path: { 'company-id': companyId },
          query: { ...query, 'filter[account_types]': [AccountType.CUSTOMER] },
        }),
      Account.parse,
    ),

  create: async (companyId: string, data: Record<string, string>): Promise<Account> => {
    const departmentId = data.department
      ? getDepartmentIdByName(data.department)
      : undefined;

    const primaryContactName =
      data.primary_contact_first_name && data.primary_contact_last_name
        ? `${data.primary_contact_first_name} ${data.primary_contact_last_name}`
        : undefined;

    const response = await postV1CompaniesCompanyIdAccounts({
      path: { 'company-id': companyId },
      body: {
        name: data.name,
        company_id: companyId,
        account_types: [AccountType.CUSTOMER], // Always CUSTOMER - no need to read from CSV
        external_id: data.external_id || null,
        accounting_id: data.accounting_id || null,
        department_id: departmentId || null,
        notes: data.notes || null,
        primary_contact: primaryContactName
          ? {
              name: primaryContactName,
              email: data.primary_contact_email || undefined,
              phone: toE164PhoneNumber(data.primary_contact_phone),
            }
          : undefined,
        billing_contact: data.billing_contact_name
          ? {
              name: data.billing_contact_name,
              email: data.billing_contact_email || undefined,
              phone: toE164PhoneNumber(data.billing_contact_phone),
            }
          : undefined,
        address: data.address_line_1
          ? {
              thoroughfare: data.address_line_1,
              premise: data.address_line_2 || undefined,
              locality: data.address_city || '',
              administrative_area: data.address_state || '',
              postal_code: data.address_zip || '',
              country: 'US',
            }
          : undefined,
        billing_address: data.billing_address_line_1
          ? {
              thoroughfare: data.billing_address_line_1,
              locality: data.billing_address_city || '',
              administrative_area: data.billing_address_state || '',
              postal_code: data.billing_address_zip || '',
              country: 'US',
            }
          : undefined,
        auto_accept_orders: false,
      },
    });

    return Account.parse(response.data.data);
  },

  update: async (
    id: string,
    data: Record<string, string>,
    companyId: string,
  ): Promise<Account> => {
    const departmentId = data.department
      ? getDepartmentIdByName(data.department)
      : undefined;

    const primaryContactName =
      data.primary_contact_first_name && data.primary_contact_last_name
        ? `${data.primary_contact_first_name} ${data.primary_contact_last_name}`
        : undefined;

    const response = await patchV1CompaniesCompanyIdAccountsId({
      path: { 'company-id': companyId, id },
      body: {
        name: data.name,
        account_types: [AccountType.CUSTOMER], // Always CUSTOMER
        external_id: data.external_id || null,
        accounting_id: data.accounting_id || null,
        department_id: departmentId || null,
        notes: data.notes || null,
        primary_contact: primaryContactName
          ? {
              name: primaryContactName,
              email: data.primary_contact_email || undefined,
              phone: toE164PhoneNumber(data.primary_contact_phone),
            }
          : undefined,
        billing_contact: data.billing_contact_name
          ? {
              name: data.billing_contact_name,
              email: data.billing_contact_email || undefined,
              phone: toE164PhoneNumber(data.billing_contact_phone),
            }
          : undefined,
        address: data.address_line_1
          ? {
              thoroughfare: data.address_line_1,
              premise: data.address_line_2 || undefined,
              locality: data.address_city || '',
              administrative_area: data.address_state || '',
              postal_code: data.address_zip || '',
              country: 'US',
            }
          : undefined,
        billing_address: data.billing_address_line_1
          ? {
              thoroughfare: data.billing_address_line_1,
              locality: data.billing_address_city || '',
              administrative_area: data.billing_address_state || '',
              postal_code: data.billing_address_zip || '',
              country: 'US',
            }
          : undefined,
      },
    });

    return Account.parse(response.data.data);
  },

  transformExport: (account: Account): Record<string, string> => {
    // Split primary contact name into first/last for export
    const primaryContactNameParts = (account.primaryContact?.name || '').split(' ');
    const primaryContactFirstName = primaryContactNameParts[0] || '';
    const primaryContactLastName = primaryContactNameParts.slice(1).join(' ') || '';

    return {
      id: account.id,
      name: account.name,
      external_id: account.externalId || '',
      department: account.department?.name || '',
      accounting_id: account.accountingId || '',
      primary_contact_first_name: primaryContactFirstName,
      primary_contact_last_name: primaryContactLastName,
      primary_contact_email: account.primaryContact?.email || '',
      primary_contact_phone: account.primaryContact?.phone || '',
      billing_contact_name: account.billingContact?.name || '',
      billing_contact_email: account.billingContact?.email || '',
      billing_contact_phone: account.billingContact?.phone || '',
      address_line_1: account.address?.streetAddress || '',
      address_line_2: account.address?.premise || '',
      address_city: account.address?.locality || '',
      address_state: account.address?.administrativeArea || '',
      address_zip: account.address?.postalCode || '',
      billing_address_line_1: account.billingAddress?.streetAddress || '',
      billing_address_city: account.billingAddress?.locality || '',
      billing_address_state: account.billingAddress?.administrativeArea || '',
      billing_address_zip: account.billingAddress?.postalCode || '',
      notes: account.notes || '',
    };
  },

  transformImport: (row: Record<string, string>): Record<string, unknown> => ({
    name: row.name?.trim(),
    external_id: row.external_id?.trim() || null,
    department: row.department?.trim() || null,
    accounting_id: row.accounting_id?.trim() || null,
    notes: row.notes?.trim() || null,
  }),

  validateRow: (row: Record<string, string>): string[] => {
    const errors: string[] = [];

    // Basic required fields
    if (!row.name?.trim()) {
      errors.push(t('csv_import_export.errors.required_field', { field: 'name' }));
    }

    errors.push(
      ...getPhoneValidationErrors(row.primary_contact_phone, 'primary_contact_phone'),
      ...getPhoneValidationErrors(row.billing_contact_phone, 'billing_contact_phone'),
    );

    if (row.notes && row.notes.length > 350) {
      errors.push(t('csv_import_export.errors.number_max', { field: 'notes', max: 350 }));
    }

    return errors;
  },
});
